import type { HTMLAttributes } from 'react';
import React, { useState } from 'react';
import classNames from 'classnames';
import { merge } from 'lodash';
import type { AnyObject } from '$util/types';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import { ReviewProvider } from '$constants/review-providers';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { DynamicRouteVrmLookup } from '$quantum/molecules/DynamicRouteVrmLookup';
import type { ContentComponent } from '$cms/ContentComponent';
import { AllianzInsured } from '$quantum/molecules/AllianzInsured';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import type { VrmLookupOnSubmitData } from '$commons/hooks/useVrmLookup';
import { useVrmLookup } from '$commons/hooks/useVrmLookup';
import Grid from '$components/layout/Grid';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { ResponsiveImage } from '$quantum/atoms/ResponsiveImage';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Icon } from '@/components/atoms/Icon';
import { Styled } from './styled';
import { getVrmLookupImage } from './utils/getVrmLookupImage';
import type { VrmLookupConfiguration, VrmLookupTranslatedEntities } from './types';
import { VrmLookupSchema, defaultConfiguration } from './types';
import { getVrmLookupHeading } from './utils/getVrmLookupHeading';

const { Row, Col } = Grid;

export interface PageTypeProps {
  readonly slug: string;
  readonly sku?: string;
  readonly type: string;
}

const allianzLogo = {
  key: 'socialTrust.image.insurance',
  title: '',
  alt: 'Allianz insurance',
  url: 'https://res.cloudinary.com/fxtr/image/upload/v1662467020/contentful/allianz-logo_wv1gt3.png',
  height: 24,
  width: 92,
};

export interface VrmLookupProps extends HTMLAttributes<HTMLDivElement> {
  readonly pageType?: PageTypeProps[];
  readonly sku?: string;
  readonly extraParams?: AnyObject;
  readonly targetBlank?: boolean;
  readonly formAction?: string;
}

/**
 * Component that renders Vrm lookup.<br />
 * https://www.notion.so/fixter/VrmLookup-049f4df5dbc34134bad2daebc71c1815
 */
export const VrmLookup: ContentComponent<
  VrmLookupTranslatedEntities,
  VrmLookupConfiguration,
  VrmLookupProps
> = function VrmLookup({
  translatedEntities,
  configuration,
  className,
  pageType,
  extraParams,
  targetBlank,
  formAction = '/vehicles/add',
}): JSX.Element {
  const {
    subheading,
    mainCta,
    invalidPostcodeMessage,
    invalidVRMMessage,
    missingPostcodeMessage,
    missingVRMMessage,
    postcodeLabel,
    vrmLabel,
    insuredText,
    insuredLogo = allianzLogo,
    googleReviewsStars,
    googleReviewsTotalReviews,
    googleReviewsLink,
    googleReviewsReviewsText,
    googleReviewsBasedOnText,
    googleReviewsLogo,
    backgroundImage,
    callUsText,
    callUsNumber,
  } = translatedEntities;
  const cmsPageContext = useCmsPageContext();
  const { reviewsProvider, sku, backgroundColor, preloadImages, lazyLoadImages } = merge(
    defaultConfiguration,
    configuration
  );

  let lookupSkus = '';
  const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;
  const collectionItemsSku = collectionItems?.product?.sku;
  if (collectionItemsSku) {
    lookupSkus = sku || collectionItemsSku;
  }

  const { onSubmit, segment } = useVrmLookup({
    extraParams,
    formAction,
    pageType,
    sku: lookupSkus,
    targetBlank,
  });

  const [{ vrm, postcode }, setState] = useState({ vrm: '', postcode: '' });

  const vrmLookupImage = getVrmLookupImage(translatedEntities, collectionItems);

  const vrmHeading = getVrmLookupHeading(translatedEntities, collectionItems, segment);

  return (
    <Styled
      className={classNames(VrmLookup.displayName, className)}
      $reviewsProvider={reviewsProvider}
      $backgroundColor={backgroundColor}
      $backgroundImageUrl={backgroundImage?.url}
    >
      <div className="card">
        <Row justify="center">
          <Col sm={12} xs={12} md={10}>
            <Row>
              <Col xs={12} sm={12} md={12} lg={vrmLookupImage ? 6 : 12} className="headings-col">
                <div className="headings">
                  <HeadingLevels className="Heading" semanticLevel={1}>
                    {vrmHeading}
                  </HeadingLevels>
                  {subheading && renderRichTextContent(subheading.content)}
                </div>
              </Col>
              {vrmLookupImage && (
                <Col xs={12} sm={12} md={12} lg={6} className="image-col">
                  <ResponsiveImage
                    className={`${VrmLookup.displayName}__img`}
                    alt=""
                    src={vrmLookupImage}
                    width={450}
                    height={350}
                    objectFit="contain"
                    fromBreakpoint="xs"
                    toBreakpoint="xxl"
                    preload={preloadImages}
                    lazy={lazyLoadImages}
                  />
                  <div className="image-shadow" />
                </Col>
              )}
            </Row>
            <Row justify="center">
              <Col sm={12} xs={12} md={10} lg={12}>
                <DynamicRouteVrmLookup
                  postcodeField={{
                    label: postcodeLabel?.value,
                    value: postcode,
                    invalidMessage: invalidPostcodeMessage?.value,
                    missingMessage: missingPostcodeMessage?.value,
                  }}
                  vrmField={{
                    label: vrmLabel?.value,
                    value: vrm,
                    invalidMessage: invalidVRMMessage?.value,
                    missingMessage: missingVRMMessage?.value,
                  }}
                  mainCta={mainCta?.value}
                  changeValueFunction={setState}
                  onSubmitForm={(data) => onSubmit(data as VrmLookupOnSubmitData)}
                  segment={segment}
                />
              </Col>
            </Row>
            <div className="footer">
              {reviewsProvider === ReviewProvider.TRUSTPILOT ? (
                <TrustPilot templateId={TRUSTPILOT_TEMPLATE_ID} />
              ) : (
                googleReviewsStars &&
                googleReviewsTotalReviews &&
                googleReviewsLink && (
                  <GoogleReviewsSummary
                    rating={googleReviewsStars?.number}
                    totalReviews={googleReviewsTotalReviews?.number}
                    link={googleReviewsLink?.value}
                    reviewsText={googleReviewsReviewsText?.value}
                    basedOnText={googleReviewsBasedOnText?.value}
                    providerLogo={googleReviewsLogo}
                    providerLogoWidth={66}
                    providerLogoHeight={24}
                  />
                )
              )}
              {insuredLogo && (
                <AllianzInsured width={92} height={24} image={insuredLogo}>
                  {insuredText?.value}
                </AllianzInsured>
              )}
              <div className="call-us">
                <Icon id="headphone" />
                <span className="call-us-text">
                  {callUsText?.value}{' '}
                  <a className="call-us-link" href={`tel:${callUsNumber?.url}`}>
                    {callUsNumber?.text}
                  </a>
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Styled>
  );
};

VrmLookup.displayName = 'VrmLookup';
VrmLookup.schema = VrmLookupSchema;
